<template>
  <button v-if="driveFileId" class="iconButton download sheetDownloadButton"
    :title="$t('receiptMatcher.download')" @click="download">
    <DownloadIcon />
  </button>
</template>

<script>
import { mapGetters } from 'vuex'
import AiService from '@/services/PoolinqAiService'
import DownloadIcon from '@/components/icons/DownloadIcon.vue'

export default {
  name: 'DocumentDownload',
  components: {
    DownloadIcon,
  },
  props: {
    /**
     * Source files returned by the AI backend, each with an optional
     * invoiceNumber and driveFileId.

     * @type {Array<{ invoiceNumber: string, driveFileId: string }>}
     */
    sourceFiles: {
      type: Array,
      default: () => [],
    },
    /**
     * The active sheet type ('outbound' or 'inbound'). Used to prefer
     * a source file matching the invoice number for inbound sheets.

     * @type {string}
     */
    sheetType: {
      type: String,
      default: 'outbound',
    },
    /**
     * The inbound invoice number, used to match the correct source file
     * and to build the downloaded document's base file name.
     *
     * @type {string|null}
     */
    invoiceNumber: {
      type: String,
      default: null,
    },
    /**
     * The outbound receipt date (YYYY-MM-DD), used to build the downloaded
     * document's base file name.
     *
     * @type {string|null}
     */
    receiptDate: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters(['getVenue']),
    /**
     * Resolves the Google Drive file id of the currently loaded sheet from the
     * source files returned by the AI backend.
     *
     * For inbound sheets the source file is matched by invoice number; for
     * outbound sheets (or when no invoice number is available) the first
     * source file with a drive file id is used.
     *
     * @returns {string|null} the drive file id or null
     */
    driveFileId() {
      if (!Array.isArray(this.sourceFiles) || this.sourceFiles.length === 0) {
        return null
      }
      // Prefer a source file matching the inbound invoice number
      if (this.sheetType === 'inbound' && this.invoiceNumber) {
        const match = this.sourceFiles.find((sf) => sf && sf.invoiceNumber === this.invoiceNumber)
        if (match && match.driveFileId) return match.driveFileId
      }
      const first = this.sourceFiles.find((sf) => sf && sf.driveFileId)
      return first ? first.driveFileId : null
    },
  },
  methods: {
    /**
     * Downloads the source document of the currently loaded sheet using its
     * Google Drive file id. The file extension is derived from the actual file
     * (MIME type, then the Content-Disposition filename, then .pdf).
     */
    async download() {
      const driveFileId = this.driveFileId
      if (!driveFileId) return
      try {
        const response = await AiService.downloadDocument(driveFileId, this.getVenue.id)
        const blob = new Blob([response.data])
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.downloadName()}${this.getFileExtension(response)}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.downloadError'),
          variant: 'danger',
        })
      }
    },
    /**
     * Builds the base file name (without extension) for the downloaded sheet
     * document, derived from the current filter context.
     */
    downloadName() {
      if (this.sheetType === 'outbound') {
        return this.receiptDate || 'receipt'
      }
      return this.invoiceNumber || 'invoice'
    },
    /**
     * Derives the file extension (including the leading dot) from the
     * download response. Priority:
     *   1. The Content-Disposition header filename, if present.
     *   2. The blob MIME type, mapped to a known extension.
     *   3. '.pdf' as a last resort.
     */
    getFileExtension(response) {
      const disposition = response && response.headers && response.headers['content-disposition']
      if (disposition) {
        const match = disposition.match(/filename="?([^";]+)"?/i)
        if (match && match[1]) {
          const ext = match[1].split('.').pop()
          if (ext && ext !== match[1]) {
            return `.${ext}`
          }
        }
      }
      const mime = response && response.data && response.data.type
      if (mime) {
        const mimeExt = {
          'application/pdf': '.pdf',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
          'application/vnd.ms-excel': '.xls',
          'text/csv': '.csv',
          'text/plain': '.txt',
          'application/zip': '.zip',
          'application/json': '.json',
        }[mime]
        if (mimeExt) return mimeExt
      }
      return '.pdf'
    },
  },
}
</script>

<style scoped>
/* The download button uses the global .iconButton base look but renders its
   icon via the DownloadIcon component, so hide the empty :before pseudo-icon
   that the global rule injects. */
button.iconButton.download:before {
  content: none;
}

button.iconButton.download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap:  ️6px;
  white-space: nowrap;
  margin-left: 6px;
}

.sheetDownloadButton {
  flex-shrink: 0;
  line-height: 0;
}
</style>